/**
 * Paddle Billing client-side integration.
 *
 * Wraps Paddle.js v2 with a Promise-based loader and helpers tailored
 * to Merq Log's single-product checkout. Avoids relying on globals
 * scattered through the app — components import { openCheckout } and
 * the library handles loading, initialization, and event passing.
 */

const PADDLE_CLIENT_TOKEN = 'live_70f5a5e3df5a1d87a527246848f'
const MERQ_LOG_PRICE_ID = 'pri_01kq3cftkvzcgfzhb6v6nmv911'
const PADDLE_JS_URL = 'https://cdn.paddle.com/paddle/v2/paddle.js'

let loadingPromise = null
let paddleInstance = null

/**
 * Load Paddle.js (idempotent — repeat calls return the same promise).
 * Resolves with the global `Paddle` object once initialized.
 */
function loadPaddle() {
  if (paddleInstance) return Promise.resolve(paddleInstance)
  if (loadingPromise) return loadingPromise

  loadingPromise = new Promise((resolve, reject) => {
    // If already on the page (e.g., hot reload), reuse the global
    if (typeof window !== 'undefined' && window.Paddle) {
      try {
        window.Paddle.Environment.set('production')
        window.Paddle.Initialize({
          token: PADDLE_CLIENT_TOKEN,
        })
        paddleInstance = window.Paddle
        resolve(paddleInstance)
        return
      } catch (err) {
        // fall through to script-load path
      }
    }

    const script = document.createElement('script')
    script.src = PADDLE_JS_URL
    script.async = true
    script.onload = () => {
      if (!window.Paddle) {
        reject(new Error('Paddle.js loaded but window.Paddle is missing'))
        return
      }
      try {
        window.Paddle.Environment.set('production')
        window.Paddle.Initialize({
          token: PADDLE_CLIENT_TOKEN,
        })
        paddleInstance = window.Paddle
        resolve(paddleInstance)
      } catch (err) {
        reject(err)
      }
    }
    script.onerror = () => reject(new Error('Failed to load Paddle.js'))
    document.head.appendChild(script)
  })

  return loadingPromise
}

/**
 * Open the Merq Log checkout overlay.
 *
 * @param {Object} opts
 * @param {string} opts.userEmail   - the user's email (for prefill)
 * @param {string} opts.userId      - Supabase user ID (passed as customData for webhook matching)
 * @param {function} [opts.onSuccess] - called when checkout completes
 * @param {function} [opts.onClose]   - called when user closes overlay (success or cancel)
 */
export async function openCheckout({ userEmail, userId, onSuccess, onClose }) {
  const Paddle = await loadPaddle()

  Paddle.Checkout.open({
    items: [
      { priceId: MERQ_LOG_PRICE_ID, quantity: 1 },
    ],
    customer: userEmail ? { email: userEmail } : undefined,
    customData: {
      // Critical: this is how the webhook matches the Paddle subscription
      // back to the Supabase user. Don't change without updating webhook.
      supabase_user_id: userId,
      product: 'merq-log',
    },
    settings: {
      displayMode: 'overlay',
      theme: 'light',
      locale: 'en',
      successUrl: window.location.origin + '/?checkout=success',
    },
    eventCallback: (event) => {
      if (event.name === 'checkout.completed') {
        onSuccess?.(event.data)
      }
      if (event.name === 'checkout.closed') {
        onClose?.(event.data)
      }
    },
  })
}

/**
 * Open Paddle's customer portal so the user can update payment methods,
 * cancel, or view invoices. Requires paddle_customer_id from profile.
 *
 * Note: Paddle's customer portal is invoked via a different mechanism —
 * we generate a portal URL server-side and redirect. For v1 this just
 * shows a friendly message directing them to email support; full portal
 * integration can be added later.
 */
export async function openManagement() {
  // TODO: implement via /api/paddle/portal serverless function once
  // we have a server-side flow for generating portal URLs.
  // For now, point them at support email.
  alert(
    'To manage your subscription, please email support@merqlog.com. ' +
    'A self-service portal is coming soon.'
  )
}

export const paddleConfig = {
  priceId: MERQ_LOG_PRICE_ID,
  productName: 'Merq Log',
  priceLabel: '$29/year',
}
